/**
 * Main entry point for StablePay Demo
 * Initializes the demo environment and renders the React application
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { App } from './App'
import { AuthProvider } from './contexts/AuthContext'
import { demoInitializer } from './utils/demoInitializer'
import { configManager } from './config'
import { 
  ImageOptimizer, 
  FontOptimizer, 
  ResourceHints, 
  PerformanceMonitor 
} from './utils/assetOptimizer'
import { AnimationPerformanceMonitor } from './utils/animationOptimizer'
import './styles/professional.css'
import './styles/skeleton-loader.css'

// Initialize demo environment
async function initializeDemo() {
  try {
    // Start performance monitoring
    PerformanceMonitor.mark('demo-init-start')
    const stopFPSMonitoring = AnimationPerformanceMonitor.startFPSMonitoring()
    
    const config = configManager.config
    
    console.log('🚀 Starting StablePay Demo', {
      environment: config.env.nodeEnv,
      buildTarget: config.env.buildTarget,
      mockApi: config.env.enableMockApi,
      features: config.demo.features,
    })
    
    // Performance optimizations
    await initializePerformanceOptimizations()
    
    // Initialize demo environment
    PerformanceMonitor.mark('demo-core-init-start')
    const result = await demoInitializer.initialize()
    PerformanceMonitor.mark('demo-core-init-end')
    
    if (result.success) {
      console.log('✅ Demo initialization completed', {
        timing: result.timing,
        warnings: result.warnings,
        coreInitTime: PerformanceMonitor.measure('demo-core-init', 'demo-core-init-start', 'demo-core-init-end')
      })
    } else {
      console.error('❌ Demo initialization failed', {
        errors: result.errors,
        timing: result.timing,
      })
    }
    
    // Render React application
    PerformanceMonitor.mark('react-render-start')
    const root = ReactDOM.createRoot(
      document.getElementById('root') as HTMLElement
    )
    
    root.render(
      <React.StrictMode>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </React.StrictMode>
    )
    PerformanceMonitor.mark('react-render-end')
    
    // Mark app as ready and setup lazy loading
    setTimeout(() => {
      document.body.classList.add('app-ready')
      ImageOptimizer.setupLazyLoading()
      
      PerformanceMonitor.mark('demo-init-end')
      const totalInitTime = PerformanceMonitor.measure('demo-total-init', 'demo-init-start', 'demo-init-end')
      const renderTime = PerformanceMonitor.measure('react-render', 'react-render-start', 'react-render-end')
      
      console.log('⚡ Performance Metrics:', {
        totalInitTime: `${totalInitTime.toFixed(2)}ms`,
        renderTime: `${renderTime.toFixed(2)}ms`,
        supportsHardwareAcceleration: AnimationPerformanceMonitor.supportsHardwareAcceleration(),
        shouldReduceAnimations: AnimationPerformanceMonitor.shouldReduceAnimations()
      })
      
      // Log detailed performance metrics after 3 seconds
      setTimeout(() => {
        PerformanceMonitor.logMetrics()
      }, 3000)
    }, 100)
    
    // Load verification scripts in development
    if (config.env.nodeEnv === 'development') {
      import('./verify-branding-integration').then(({ verifyBrandingIntegration }) => {
        setTimeout(verifyBrandingIntegration, 2000)
      }).catch(console.error)
      
      import('./verify-performance-optimization').then(({ verifyPerformanceOptimization }) => {
        setTimeout(verifyPerformanceOptimization, 3000)
      }).catch(console.error)
    }
    
  } catch (error) {
    console.error('💥 Fatal error during demo initialization:', error)
    
    // Show error message to user
    const root = document.getElementById('root')
    if (root) {
      root.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          padding: 20px;
          text-align: center;
          font-family: Inter, sans-serif;
        ">
          <div style="
            background: #fee2e2;
            border: 1px solid #fecaca;
            border-radius: 8px;
            padding: 24px;
            max-width: 500px;
            margin-bottom: 20px;
          ">
            <h2 style="color: #dc2626; margin-bottom: 12px;">
              Demo Initialization Failed
            </h2>
            <p style="color: #7f1d1d; margin-bottom: 16px;">
              There was an error starting the StablePay demo. Please refresh the page to try again.
            </p>
            <details style="text-align: left;">
              <summary style="cursor: pointer; color: #dc2626; font-weight: 500;">
                Technical Details
              </summary>
              <pre style="
                background: #f3f4f6;
                padding: 12px;
                border-radius: 4px;
                margin-top: 8px;
                font-size: 12px;
                overflow: auto;
                color: #374151;
              ">${error instanceof Error ? error.stack : String(error)}</pre>
            </details>
          </div>
          <button 
            onclick="window.location.reload()" 
            style="
              background: #0066cc;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 6px;
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
            "
          >
            Refresh Page
          </button>
        </div>
      `
    }
  }
}

// Performance optimization initialization
async function initializePerformanceOptimizations(): Promise<void> {
  // Setup resource hints for better loading
  ResourceHints.addDNSPrefetch([
    'fonts.googleapis.com',
    'fonts.gstatic.com'
  ])
  
  // Font preloading disabled for demo - using system fonts
  // FontOptimizer.preloadFonts([
  //   { family: 'Inter', weight: '400' },
  //   { family: 'Inter', weight: '500' },
  //   { family: 'Inter', weight: '600' },
  //   { family: 'Inter', weight: '700' }
  // ])
  
  // Preload critical images
  const criticalImages = [
    '/stablepay-logo-landscape.png',
    '/stablepay-logo-portrait.png'
  ]
  
  try {
    await ImageOptimizer.preloadCriticalImages(criticalImages)
  } catch (error) {
    console.warn('Some critical images failed to preload:', error)
  }
  
  // Setup service worker for caching (if available)
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    try {
      await navigator.serviceWorker.register('/demo-sw.js')
      console.log('✅ Service worker registered for caching')
    } catch (error) {
      console.warn('Service worker registration failed:', error)
    }
  }
}

// Start initialization
initializeDemo()

// Hot module replacement for development
if (import.meta.hot) {
  import.meta.hot.accept('./App', () => {
    console.log('🔄 Hot reloading App component')
  })
  
  import.meta.hot.accept('./utils/demoInitializer', () => {
    console.log('🔄 Hot reloading demo initializer')
  })
}